import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { CheckApiRead } from 'services/Generic/Generic';
import { ApiType, getApiURL } from 'services/Instances/configuration'
import { Auth } from 'services/Instances/types'

const BROWSER_URL = {
  HOM: '10.200.12.244:1917',
  QA_INT: '10.200.12.244:9347'
}

export const errClosedSession = 'Sua sessão expirou, você será redirecionado para a página de login!';
const tokenValidationErrorMessage = 'Ocorreu um erro ao tentar renovar a sua sessão, você será redirecionado para a página de login!';

export const getEnvironment = (): 'hom' | 'dev' | 'qa' | 'qa_int' => {
  const { host } = window.location

  if (host.includes(BROWSER_URL.HOM)) {
    return 'hom'
  } else if (host.includes('csn')) {
    return host.split('-')[2] as 'dev' | 'qa'
  } else if (host.includes(BROWSER_URL.QA_INT)) {
    return 'qa_int'
  }

  return 'dev'
}

export const NO_TOKEN_URL = ['#/', '#/fornecedor/access', '#/ngt/access']
export const IGNORE_TOKEN_VALIDATION = ['#/recuperar/']

const getAuth = (): Auth => {
  if (!window.localStorage.getItem('persist:root')) return null;
  const ref = JSON.parse(window.localStorage.getItem('persist:root'))
  if (!ref?.auth) return null;
  const auth = JSON.parse(ref?.auth) as Auth

  return auth
}

export const requestHandler = async (config) => {
  const { 'csn-tk': token } = window.localStorage;

  if (!NO_TOKEN_URL.includes(window.location.hash)) {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${token}`
    }
  }

  return config
}

export const responseHandler = async (response: AxiosResponse) => {
  return response
}

type CustomAxiosRequestConfig = AxiosRequestConfig & {
  isRetry?: boolean
}

export const responseErrorHandler = async (error: AxiosError | null): Promise<any> => {

  if (!error) throw new Error("Houve um erro ao fazer a requisição.")

  const originalRequest = error.config as CustomAxiosRequestConfig
  let isRetryAttempt = originalRequest?.isRetry
  const shouldRetryAuthentication = (error?.response?.status && (error.response?.status === 401 || error.response?.status === 403)) && !isRetryAttempt

  // Is not unauthorized or is unauthorized but is retry attempt
  const errMessage = error.response?.data?.title ? { message: error.response?.data?.title, status: error.response?.data?.status } : error
  if (!shouldRetryAuthentication) {
    if (error?.response?.status === 500 || error?.response?.status === 503) {
      throw new Error(error.response?.data?.mensagem || "Falha na resposta do servidor. Tente novamente em instantes.")
    }

    // If error equals 'NETWORK ERROR', it seens client has no internet connection
    if (error.message === "Network Error") {
      connectionLostError()
      return Promise.reject({ message: "NetworkError" })
    }

    return Promise.reject(errMessage)
  }

  const rememberUser = window.sessionStorage.getItem('csn-session') === 'true'
  const authData = getAuth()

  if (!authData) return refreshTokenError(errClosedSession);

  const isCsnUser = authData?.data?.Perfil === 'Csn'

  // If user is not remembered or is not CSN user, warn user that he will be logged out
  const isRememberedOrCsnUser = rememberUser || isCsnUser

  // It's activate a modal alerting user that he will be logged out
  if (!isRememberedOrCsnUser) return refreshTokenError(errClosedSession);

  // Retrieve token and refresh token from local storage
  const { 'csn-rtk': refreshToken, 'csn-tk': token } = window.localStorage
  const loginRefreshTokenUrl = `${getApiURL(ApiType.Login)}RefreshToken`
  if (!authData.user) return refreshTokenError(errClosedSession);

  const loginCredentials = {
    email: authData.user?.TxEmail,
    perfilUsuarioId: authData.user?.PerfilUsuarioId?.toString(),
    refreshToken,
    token: token || authData.token,
  }

  // Set retry attempt to true to avoid infinite loop
  isRetryAttempt = true

  try {
    if (!loginCredentials.token) loginCredentials.token = window.localStorage.getItem('csn-tk');
    if (!loginCredentials.refreshToken) return refreshTokenError(errClosedSession);
    const response = await axios.post(loginRefreshTokenUrl, loginCredentials)

    // If response is not 200, throw an error
    if (response.status !== 200) return refreshTokenError();

    // Update local storage with new token and refresh token
    window.localStorage.setItem('csn-time', new Date().toString())
    window.localStorage.setItem('csn-tk', response.data.token)
    window.localStorage.setItem('csn-rtk', response.data.refreshToken)

    // Update original request with new token
    originalRequest.headers['Authorization'] = `Bearer ${response.data.token}`

    // Retry original request
    const retryRequestPromise = axios(originalRequest)
    return Promise.resolve(retryRequestPromise)
  } catch (error: any) {
    // It's activate a modal alerting user that he will be logged out
    if (hasTokenRefreshError())
      return Promise.reject({ message: null });
    return refreshTokenError(errClosedSession);
  }
}

export const hasTokenRefreshError = () => {
  return window.localStorage.getItem('csn-refresh-token-unauthorized-message');
}

export const hasConnectionLostError = () => {
  return window.localStorage.getItem('csn-api-connection-lost');
}

export const clearConnectionLostError = () => {
  window.localStorage.removeItem('csn-api-connection-lost')
  window.localStorage.removeItem('connErrorIsVisible')
}

export const refreshTokenError = (msg?: string) => {
  return window.localStorage.setItem(
    'csn-refresh-token-unauthorized-message',
    msg ?? tokenValidationErrorMessage
  )
}

export const connectionLostError = () => {
  return window.localStorage.setItem('csn-api-connection-lost', 'true')
}

export const isTokenRequired = (hash: string, token: string) => {
  const tokenRequired = !IGNORE_TOKEN_VALIDATION.some(substring => hash.includes(substring))
  const hasNoToken = (!token || token == '')
  return tokenRequired && hasNoToken
}

export const verifyApiHealth = async (): Promise<void> => {
  const auth = getAuth()
  let connected = false
  const maxRetry = 20
  const MIN_AWAIT = 6000
  let tryNum = 1
  try {
    do {
      const initialTime = performance.now();
      connected = await CheckApiRead(auth?.csn?.NmMatricula, auth?.fornecedor?.UsuarioId)
      const endTime = performance.now()
      const elapsedTime = endTime - initialTime
      tryNum++
      if (!connected && elapsedTime < MIN_AWAIT) await Delay(MIN_AWAIT - elapsedTime)
    } while (!connected && tryNum < maxRetry)
    return connected ? Promise.resolve() : Promise.reject()
  } catch (error) {
    return Promise.reject();
  }
}

export const Delay = (millis: number) => {
  return new Promise(resolve => setTimeout(resolve, millis))
}